import { Suspense } from 'react'
import { Outlet, useLocation } from 'react-router-dom'

// Imported from their modules rather than the `@/components/layout` barrel:
// that barrel re-exports `AppShell`, which aliases this file, and going through
// it would create an import cycle.
import { Footer } from '@/components/layout/footer'
import { Header } from '@/components/layout/header'
import { Sidebar } from '@/components/layout/sidebar'
import { ErrorBoundary, OfflineBanner, PageLoader } from '@/components/feedback'
// Direct hook import: the `@/hooks` barrel re-exports every module hook, which
// would drag the feature modules into the eager shell chunk and undo the
// per-route code splitting.
import { useIsMobile } from '@/hooks/use-is-mobile'
import { useSidebarStore } from '@/stores'
import { cn } from '@/utils'

/**
 * Authenticated application shell: sidebar, header, footer.
 *
 * The boundary and Suspense sit *inside* the chrome so a failing or loading
 * page never takes the navigation down with it. Keying the boundary on the
 * pathname clears a caught error when the user navigates away.
 */
export function DashboardLayout() {
  const collapsed = useSidebarStore((s) => s.collapsed)
  const isMobile = useIsMobile()
  const { pathname } = useLocation()

  return (
    <div className="bg-background min-h-screen">
      <Sidebar />
      <div
        className={cn(
          'flex min-h-screen flex-col transition-[padding] duration-300',
          isMobile
            ? 'pl-0'
            : collapsed
              ? 'pl-[var(--sidebar-collapsed-width)]'
              : 'pl-[var(--sidebar-width)]',
        )}
      >
        <OfflineBanner />
        <Header />
        <main className="relative flex-1 p-4 sm:p-6">
          <ErrorBoundary key={pathname} boundary={`route:${pathname}`}>
            <Suspense fallback={<PageLoader />}>
              <Outlet />
            </Suspense>
          </ErrorBoundary>
        </main>
        <Footer />
      </div>
    </div>
  )
}
